import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { CdkAccordionModule } from '@angular/cdk/accordion';
import {
  ActivatedRoute,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import { AccountManagementService } from '@core/services/account-management.service';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { BreadCrumbService } from '@core/services/bread-crumb.service';
import { NotificationFacade } from '@core/facades/notification.facade';
import { AccountKYC } from '@core/models/admin/account/account.interface';
import { IndividualTabsComponent } from '@shared/ui/individual-tabs/individual-tabs.component';
import { ButtonDirective } from '@shared/directives/button.directive';
import { LoadingService } from '@core/services/loading.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Clipboard } from '@angular/cdk/clipboard';
import {
  DropDownModel,
  DropdownComponent,
} from '@shared/ui/dropdown/dropdown.component';
import { StatusComponent } from '../../shared/ui/status/status.component';
import { environment } from '@core/environments/environment';
import { PermissionDirective } from '@shared/directives/permission.directive';
import { RolesAndPermissionsService } from '@core/services/roles-and-permissions.service';

@Component({
  selector: 'app-individual-account',
  standalone: true,
  imports: [
    CdkAccordionModule,
    CommonModule,
    IndividualTabsComponent,
    RouterOutlet,
    RouterLink,
    ButtonDirective,
    RouterLinkActive,
    NgxSkeletonLoaderModule,
    DropdownComponent,
    StatusComponent,
    PermissionDirective
  ],
  templateUrl: './individual-account.component.html',
  styleUrl: './individual-account.component.scss',
})
export class IndividualAccountComponent implements OnInit, OnDestroy {
  accountData!: AccountKYC | null;
  subs: Subscription[] = [];
  accountId!: string;
  userDetailsUrl!: string;
  accountsUrl!: string;
  accountNumber!: string;
  incomeCategory!: string;

  isFetchingAccountInfo$!: Observable<boolean>;

  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private breadCrumbService = inject(BreadCrumbService);
  private accountService = inject(AccountManagementService);
  private cdr = inject(ChangeDetectorRef);
  private toast = inject(NotificationFacade);
  private loadingService = inject(LoadingService);
  private clipboard = inject(Clipboard);
  private permissionService = inject(RolesAndPermissionsService);

  constructor() {
    this.isFetchingAccountInfo$ = this.loadingService.getLoadingObservable(
      'get-individual-account-kyc'
    );
  }

  ngOnInit(): void {
    this.breadCrumbService.setPageBreadCrumb(this.activatedRoute);

    this.accountId = this.activatedRoute.snapshot.params['accountId'];
    if (this.accountId) {
      this.userDetailsUrl = `/admin/accounts/individual/${this.accountId}`;
      this.accountsUrl = `/admin/accounts/individual/accounts/${this.accountId}`;
      this.getAccountInfo();
      this.cdr.detectChanges();
    }
  }

  _editAccount() {
    this.router.navigateByUrl(
      `admin/accounts/edit?profileId=${this.accountData?.user_id}&accountId=${this.activatedRoute.snapshot.params['accountId']}&edit=true`
    );
  }

  editAccount() {
    // console.log('edit account');

    this.router.navigateByUrl(
      `/admin/accounts/individual/edit/${this.accountId}`
    );
  }
  getIncomeCategory(annualIncome: string | undefined): string {
    if (!annualIncome) {
      return '—';
    }
    if (annualIncome.includes('Below')) {
      return 'RETAIL';
    }
    if (annualIncome.includes('Above')) {
      return 'HNI';
    }
    const range = annualIncome.split(' - ')[1];
    const upperBound = parseInt(range.replace(/,/g, ''), 10);
    return upperBound > 50000000 ? 'HNI' : 'RETAIL';
  }

  getAccountInfo(): void {
    const sub = this.accountService
      .getIndividualAccountInfo(this.accountId)
      .subscribe({
        next: (response: any) => {
          this.accountData = response.data as AccountKYC;
          this.accountService.setAccountKYCData(this.accountData);
          this.accountNumber = response.data.mango_account_number;

          this.incomeCategory = this.getIncomeCategory(this.accountData?.user?.annual_income || '');
          this.cdr.detectChanges();
        },
        error: () => {
          this.toast.error('Failed to fetch account info');
        },
      });

    this.subs.push(sub);
  }

  getProfileImageUrl(): string {
    return `${environment.FILE_BASE_URL}${this.accountData?.user?.profile_image}`;
}

  // getAccountKycTier() {
  //   const sub = this.accountService
  //     .getAccountKycTier(this.accountId)
  //     .subscribe({
  //       next: (response) => {
  //         this.tierLevelSubject$.next(response.data.kyc_levels);
  //       },
  //       error: () => {
  //         this.toast.error('Failed to fetch tier level');
  //       },
  //     });

  //   this.subs.push(sub);
  // }
  copy(accountNumber: string) {
    this.clipboard.copy(accountNumber);
    this.toast.success('Successfully copied to clipboard');
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '—';

    const date = new Date(dateString);

    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  goBack() {
    history.back();
  }

  combinedActions(): DropDownModel[] {
    return [
      {
        title: 'Edit Profile',
        action: () => {
          this.editAccount();
        },
        show: true && !!this.accountId && this.permissionService.hasPermission('get_individual'),
      },
      {
        title: 'Cash Statement',
        action: () => {
          this.router.navigate(['/admin/accounts/generate-cash-statement'], {
            queryParams: {
              accountId: this.accountId,
              accountType: "individual",
            },
          });
        },
        show: true && this.permissionService.hasPermission('can_generate_cash_statement'),
      },
      {
        title: 'Export Report',
        show: true && (this.permissionService.hasPermission('export_individual_kyc_data') || this.permissionService.hasPermission('transaction_statement') || this.permissionService.hasPermission('can_view_nav_reports') || this.permissionService.hasPermission('can_view_order_reports')),
        children: [
          {
          title: 'Client Directory',
          action: () => {
            this.router.navigateByUrl(
              `/admin/accounts/client-directory-report/individual/${this.accountId}`
            );
          },
          show: true && this.permissionService.hasPermission('export_individual_kyc_data'),
        },
        {
          title: 'Account Statement',
          action: () => {
            this.router.navigateByUrl(
              `/admin/accounts/accocunt-statement-report/${this.accountId}`
            );
          },
          show: true && this.permissionService.hasPermission('transaction_statement'),
        },
        {
          title: 'Nav',
          action: () => {
            this.router.navigateByUrl(
              `/admin/accounts/nav-report/${this.accountId}`
            );
          },
          show: true && this.permissionService.hasPermission('can_view_nav_reports'),
        },
        {
          title: 'Order',
          action: () => {
            this.router.navigateByUrl(
              `/admin/accounts/order-report/${this.accountId}`
            );
          },
          show: true && this.permissionService.hasPermission('can_view_order_reports'),
        },
        // {
        //   title: 'Audit Log',
        //   action: () => {
        //     this.router.navigateByUrl('/admin/accounts/joint-profile-details');
        //   },
        //   show: true,
        // },
        ]
      }
    ];
  }

  generateVirtualAccount() {
    const sub = this.accountService.generateVirtualAccountNumber(this.accountId).subscribe({
      next: (response) => {
        this.toast.success('Virtual account generation queued successfully, it may take a few moments to reflect');
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.toast.error(error?.error?.message || 'Failed to generate virtual account');
      },
    });

    this.subs.push(sub);
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
